import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import { FormEvent, useState } from 'react';
import { sendFeedbackConfirmed } from '~/utils/error-diagnostics';

type SendState = 'idle' | 'sending' | 'sent' | 'failed';

type ErrorReportDialogProps = {
    open: boolean;
    onClose: () => void;
    /** Sentry event ID of the crash the report is attached to. */
    eventId: string;
};

/**
 * Lets the user describe what they were doing when the error occurred. The
 * description is sent to Sentry as user feedback linked to the crash event.
 * Built in-app because the Sentry-hosted report dialog is blocked by our CSP.
 */
const ErrorReportDialog = ({
    open,
    onClose,
    eventId,
}: ErrorReportDialogProps) => {
    const [description, setDescription] = useState('');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [sendState, setSendState] = useState<SendState>('idle');

    const canSend =
        description.trim().length > 0 &&
        sendState !== 'sending' &&
        sendState !== 'sent';

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        if (!canSend) {
            return;
        }
        setSendState('sending');
        const delivered = await sendFeedbackConfirmed({
            message: description.trim(),
            name: name.trim() || undefined,
            email: email.trim() || undefined,
            associatedEventId: eventId,
        });
        setSendState(delivered ? 'sent' : 'failed');
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <form onSubmit={handleSubmit}>
                <DialogTitle>Send error report</DialogTitle>
                <DialogContent
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                >
                    <Typography variant="body2" color="text.secondary">
                        The technical details of this error have already been
                        sent. Describing what you were doing helps us reproduce
                        it. Do not include patient names or other patient
                        identifiers.
                    </Typography>
                    <TextField
                        label="What were you doing when the error occurred?"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        required
                        multiline
                        minRows={4}
                        autoFocus
                        disabled={sendState === 'sent'}
                    />
                    <TextField
                        label="Name (optional)"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        disabled={sendState === 'sent'}
                    />
                    <TextField
                        label="E-mail for follow-up (optional)"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={sendState === 'sent'}
                    />
                    {sendState === 'sent' && (
                        <Alert severity="success">
                            Thank you. Your report was sent with error ID{' '}
                            {eventId}.
                        </Alert>
                    )}
                    {sendState === 'failed' && (
                        <Alert severity="error">
                            The report could not be sent. Please use &quot;Copy
                            details&quot; and e-mail them to support.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>
                        {sendState === 'sent' ? 'Close' : 'Cancel'}
                    </Button>
                    {sendState !== 'sent' && (
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={!canSend}
                        >
                            {sendState === 'sending' ? 'Sending…' : 'Send'}
                        </Button>
                    )}
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default ErrorReportDialog;
